import { JS_SPACE, javaIsBlank, javaLines, javaStrip } from '../javaText.ts';
import { writeJson, type JsonValue } from '../jsonWriter.ts';
import { InterchangeError, JavaNumber, javaValueOf, readJavaJson, type JavaJson } from '../tables/jsonReader.ts';
import { GENERATORS, GENERATOR_KEYS, type GeneratorKey } from './generators.ts';

/**
 * Script packs - Quick Generator sources and saved scripts in one JSON file - as the
 * desktop's `interchange.ScriptBundleCodec` writes and reads them, with its notes and
 * errors in its words (schema/fixtures/script-pack-cases.json):
 *
 *     { "format": "realmery-scripts", "version": 1, "exportedBy": "...",
 *       "generators": [ { "key": "TAVERN", "name": "Tavern", "function": "generate_tavern", "source": "..." } ],
 *       "scripts":    [ { "name": "...", "description": "...", "language": "javascript", "source": "..." } ] }
 *
 * A script says its language only when it isn't Python, so packs from older versions read as
 * they always did. Reading never runs anything: the import shows every source first.
 */

export const SCRIPT_PACK_FORMAT = 'realmery-scripts';
const FORMAT_VERSION = 1;

export const PYTHON = 'python';
export const JAVASCRIPT = 'javascript';

export interface PackGenerator {
  readonly key: GeneratorKey;
  readonly source: string;
}

export interface PackScript {
  readonly name: string;
  readonly description: string;
  readonly source: string;
  /** "python" (the desktop's), "javascript" (the web's), or whatever the file said. */
  readonly language: string;
}

export interface ScriptPack {
  readonly generators: readonly PackGenerator[];
  readonly scripts: readonly PackScript[];
}

/** `ScriptBundle.ScriptItem`: a blank name is "Imported Script"; no language is Python. */
export function packScript(name: string | null, description: string | null, source: string | null, language: string | null = null): PackScript {
  return {
    name: name === null || javaIsBlank(name) ? 'Imported Script' : javaStrip(name),
    description: description === null ? '' : javaStrip(description),
    source: source ?? '',
    language: language === null || javaIsBlank(language) ? PYTHON : javaStrip(language).toLowerCase(),
  };
}

export function writeScriptPack(pack: ScriptPack, exportedBy: string | null): string {
  const root = new Map<string, JsonValue>([
    ['format', SCRIPT_PACK_FORMAT],
    ['version', FORMAT_VERSION],
  ]);
  if (exportedBy !== null && !javaIsBlank(exportedBy)) root.set('exportedBy', exportedBy);
  root.set(
    'generators',
    pack.generators.map(
      (g) =>
        new Map<string, JsonValue>([
          ['key', g.key],
          ['name', GENERATORS[g.key].name],
          ['function', GENERATORS[g.key].functionName],
          ['source', g.source],
        ]),
    ),
  );
  root.set(
    'scripts',
    pack.scripts.map((s) => {
      const map = new Map<string, JsonValue>([
        ['name', s.name],
        ['description', s.description],
      ]);
      // Only a web edition script says so; a file with none is Python, as every older one is.
      if (s.language !== PYTHON) map.set('language', s.language);
      map.set('source', s.source);
      return map;
    }),
  );
  return writeJson(root);
}

export interface ScriptPackRead {
  readonly pack: ScriptPack;
  /** Anything skipped or worth knowing, for the import preview. */
  readonly notes: readonly string[];
}

/** @throws InterchangeError if `text` isn't JSON, or holds no generators or scripts */
export function readScriptPack(text: string | null): ScriptPackRead {
  const root = readJavaJson(javaStrip(text ?? ''));
  if (!(root instanceof Map)) throw new InterchangeError("This file isn't a Realmery generator or script export.");
  const notes: string[] = [];
  const generators: PackGenerator[] = [];
  const scripts: PackScript[] = [];
  const generatorList = root.get('generators');
  const scriptList = root.get('scripts');
  if (Array.isArray(generatorList) || Array.isArray(scriptList)) {
    const format = root.get('format');
    if (format !== undefined && format !== null && format !== SCRIPT_PACK_FORMAT) notes.push(`The file says its format is "${javaValueOf(format)}"; read it as a script export anyway.`);
    const version = root.get('version');
    if (version instanceof JavaNumber && intValue(version) > FORMAT_VERSION) notes.push("This file was made by a newer Realmery; anything this version doesn't understand was left out.");
    if (Array.isArray(generatorList)) for (const item of generatorList) readGenerator(item, generators, notes);
    if (Array.isArray(scriptList)) for (const item of scriptList) readScript(item, scripts, notes);
  } else if (root.has('source') && (root.has('key') || root.has('function'))) {
    readGenerator(root, generators, notes);
  } else if (root.has('source')) {
    readScript(root, scripts, notes);
  } else {
    throw new InterchangeError(`This file isn't a Realmery generator or script export - it has no "generators", "scripts", or "source".`);
  }
  if (generators.length === 0 && scripts.length === 0) throw new InterchangeError('There are no generators or scripts in this file.');
  return { pack: { generators, scripts }, notes };
}

function readGenerator(item: JavaJson, out: PackGenerator[], notes: string[]): void {
  const source = item instanceof Map ? item.get('source') : undefined;
  if (!(item instanceof Map) || typeof source !== 'string') {
    notes.push('Skipped a generator with no source.');
    return;
  }
  const key = resolveKey(item.get('key')) ?? resolveKey(item.get('function')) ?? resolveKey(item.get('name'));
  if (!key) {
    const label = item.get('key') ?? item.get('function') ?? item.get('name') ?? null;
    notes.push(`Skipped generator "${javaValueOf(label)}" - this version of Realmery has no generator by that name. (It could be imported as a saved script instead.)`);
    return;
  }
  if (out.some((existing) => existing.key === key)) {
    notes.push(`The file has more than one ${GENERATORS[key].name} generator; kept the first.`);
    return;
  }
  out.push({ key, source });
}

function readScript(item: JavaJson, out: PackScript[], notes: string[]): void {
  const source = item instanceof Map ? item.get('source') : undefined;
  if (!(item instanceof Map) || typeof source !== 'string') {
    notes.push('Skipped a script with no source.');
    return;
  }
  const name = item.get('name');
  const description = item.get('description');
  const language = item.get('language');
  out.push(packScript(name === undefined || name === null ? null : javaValueOf(name), description === undefined || description === null ? null : javaValueOf(description), source, typeof language === 'string' ? language : null));
}

/** "TAVERN", "tavern", "generate_tavern" and "Tavern" all find the Tavern generator. */
export function resolveKey(value: JavaJson | undefined): GeneratorKey | null {
  if (value === undefined || value === null) return null;
  const text = javaStrip(javaValueOf(value));
  return GENERATOR_KEYS.find((key) => key.toLowerCase() === text.toLowerCase() || GENERATORS[key].functionName.toLowerCase() === text.toLowerCase() || GENERATORS[key].name.toLowerCase() === text.toLowerCase()) ?? null;
}

function intValue(number: JavaNumber): number {
  if (number.isLong) return Number(BigInt.asIntN(32, number.long));
  if (Number.isNaN(number.value)) return 0;
  return Math.trunc(Math.max(-(2 ** 31), Math.min(2 ** 31 - 1, number.value)));
}

// --- ScriptCompatibility: Python that won't run in the desktop's interpreter ---

const IMPORT = new RegExp(`^${JS_SPACE}*(?:import${JS_SPACE}+([\\w.]+(?:${JS_SPACE}*,${JS_SPACE}*[\\w.]+)*)|from${JS_SPACE}+([\\w.]+)${JS_SPACE}+import\\b)`);

/** The desktop's warnings for a Python script that imports modules its interpreter hasn't. */
export function pythonWarnings(source: string | null): string[] {
  const modules = new Set<string>();
  let inTripleQuote = false;
  for (const line of javaLines(source ?? '')) {
    const quotes = countTripleQuotes(line);
    if (inTripleQuote) {
      if (quotes % 2 === 1) inTripleQuote = false;
      continue;
    }
    if (quotes % 2 === 1) {
      inTripleQuote = true;
      continue;
    }
    const match = IMPORT.exec(line);
    if (!match) continue;
    const names = match[1] ?? match[2]!;
    for (const name of names.split(',')) {
      const module = javaStrip(name).split('.')[0]!;
      if (module !== '' && module !== '__future__') modules.add(module);
    }
  }
  return [...modules].map((module) =>
    module === 'random'
      ? `Imports "random", but Realmery's built-in Python has no standard library, so this line will fail when the script runs. Use the built-in rand_choice(list) and rand_int(low, high) instead - they need no import.`
      : `Imports "${module}", but Realmery's built-in Python has no standard library or extra packages, so this line will fail when the script runs.`,
  );
}

function countTripleQuotes(line: string): number {
  let count = 0;
  for (const marker of ['"""', "'''"]) {
    let index = line.indexOf(marker);
    while (index >= 0) {
      count++;
      index = line.indexOf(marker, index + 3);
    }
  }
  return count;
}

/** A script's language as people say it: "Python", "JavaScript", or whatever a file called it. */
export function languageName(language: string): string {
  if (language === PYTHON) return 'Python';
  if (language === JAVASCRIPT) return 'JavaScript';
  return language;
}
